import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useIntl} from "react-intl";
import dayjs from "dayjs";

import {IListTableCol} from "@housebets/housebets-x-package/lib/components/ui/tables/Table/types";
import {ITabButton} from "@housebets/housebets-x-package/lib/components/ui/tabs/Tabs/types";
import {AmountValue, GameIcon, NameWithVip, TableTd} from "@housebets/housebets-x-package";
import {ActivityIcon} from "@housebets/housebets-x-package/lib/components/icons";

import apiWallet from "app/api/wallet";
import apiGameHistory from "app/api/gameHistory";
import {useAppSelector} from "hooks/useAppSelector";
import {LatestBetOrder} from "app/store/slices/game";
import {convertToFiat} from "app/utils/help";

import {mutateHighRollerData} from "../utils/high-roller.mutation";
import {mutateMessageData} from "../utils/message.mutation";

import {IAllBetDTO, IHighRollersDTO, ILiveBetsItem, IMyBetsDTO} from "../types";
import {mutateAllBetsData} from "../utils/all-bets.mutation";
import {mutateMyBetsData} from "../utils/my-bets.mutation";
import {useDebounce} from "app/hooks/useDebounce";

export type TLiveBetsTab = "LiveBets" | "BigBets" | "MyBets";

export const useLiveBetsData = () => {
    const {$t} = useIntl();
    const {latestBetOrder, highRollers} = useAppSelector((state) => state.game);
    const {user} = useAppSelector((state) => state.user);
    const isFiat = useAppSelector((state) => state.setting.isFiat);
    const balance = useAppSelector((state) => state.user.balance);
    const [currentTab, setCurrentTab] = useState<TLiveBetsTab>("LiveBets");
    const [isHovered, setIsHovered] = useState(false);
    const [, setTableMaxLength] = useState(50);

    const [allBetsData, setAllBetsData] = useState<ILiveBetsItem[]>([]);
    const [highRollersData, setHighRollersData] = useState<ILiveBetsItem[]>([]);
    const [myBetsData, setMyBetsData] = useState<ILiveBetsItem[]>([]);
    const [isAllBetsLoading, setIsAllBetsLoading] = useState(false);
    const [isHighRollersLoading, setIsHighRollersLoading] = useState(false);
    const [isMyBetsLoading, setIsMyBetsLoading] = useState(false);

    const getAllBetsData = async () => {
        setIsAllBetsLoading(true);

        try {
            const {status, data} = await apiGameHistory.getUserGameRecordsList(1, 50, 1, 0);
            if (status === 200 && data.code === 0) {
                const arr: IAllBetDTO[] = data?.data?.list || [];
                const mapped = arr.map<ILiveBetsItem>((item, i) => mutateAllBetsData(item, i));
                setAllBetsData(mapped.reverse());
            }
        } finally {
            setIsAllBetsLoading(false);
        }
    };

    const getBigBetsData = async () => {
        try {
            setIsHighRollersLoading(true);
            const {status, data} = await apiGameHistory.queryGameConsume(3);
            if (status === 200 && data.code === 0) {
                const arr: IHighRollersDTO[] = data?.data?.data || [];
                const mapped = arr.map<ILiveBetsItem>((item, i) => mutateHighRollerData(item, i));
                setHighRollersData(mapped.reverse());
            }
        } finally {
            setIsHighRollersLoading(false);
        }
    };

    const getMyBetsData = async () => {
        try {
            setIsMyBetsLoading(true);
            const {status, data} = await apiWallet.queryUserBetHistoryList(1, 50);
            if (status === 200 && data.code === 0) {
                const arr: IMyBetsDTO[] = data?.data?.list || [];
                const mapped = arr.map<ILiveBetsItem>((item, i) =>
                    mutateMyBetsData(item, i, {
                        name: user?.nick || "",
                        level: user?.level || 0,
                    }),
                );
                setMyBetsData(mapped.reverse());
            }
        } finally {
            setIsMyBetsLoading(false);
        }
    };

    const updateAllBetsMessageData = useCallback(
        (latestBestData: LatestBetOrder) => {
            if ([9001, 8000].includes(latestBestData.gameid)) {
                return;
            }

            const order: ILiveBetsItem = mutateMessageData(latestBestData, allBetsData.length);
            setAllBetsData((old) => [
                ...old,
                {
                    ...order,
                    dark: !old[old.length - 1]?.dark,
                },
            ]);
        },
        [allBetsData],
    );

    const updateHighRollersMessageData = useCallback(
        (latestBestData: LatestBetOrder) => {
            if ([9001, 8000].includes(latestBestData.gameid)) {
                return;
            }

            const order: ILiveBetsItem = mutateMessageData(latestBestData, highRollersData.length);
            setHighRollersData((old) => [
                ...old,
                {
                    ...order,
                    dark: !old[old.length - 1]?.dark,
                },
            ]);
        },
        [highRollersData],
    );

    useDebounce(
        () => {
            if (currentTab === "LiveBets" && latestBetOrder) {
                updateAllBetsMessageData(latestBetOrder);
            }
        },
        1000,
        [latestBetOrder],
    );

    useDebounce(
        () => {
            if (currentTab === "BigBets" && highRollers) {
                updateHighRollersMessageData(highRollers);
            }
        },
        1000,
        [highRollers],
    );

    const data = useMemo(() => {
        return currentTab === "LiveBets" ? allBetsData : currentTab === "BigBets" ? highRollersData : myBetsData;
    }, [currentTab, allBetsData, highRollersData, myBetsData]);

    const gameIcons: Record<string, React.ComponentType> = {};

    const columns = useMemo<IListTableCol[]>(() => {
        if (currentTab === "MyBets") {
            return [
                {
                    key: "game",
                    header: () => $t({id: "Game"}),
                    col: ({name, game}) => (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                            }}>
                            <GameIcon icon={'Game' + name} />
                            <div className="eclipse">{name}</div>
                        </div>
                    ),
                    width: {
                        sm: 200,
                        md: 180,
                        lg: 219,
                    },
                },
                {
                    key: "time",
                    header: () => $t({id: "Time"}),
                    col: (value) => <div>{dayjs(value).format("YYYY/MM/DD HH:mm:ss")}</div>,
                    width: {
                        sm: 223,
                        md: 180,
                        lg: 223,
                    },
                },
                {
                    key: "wager",
                    header: () => $t({id: "BetAmount"}),
                    col: ({currency, icon, value}, {tokenId, multiplier}) => (
                        <TableTd type={multiplier.growth === "+" ? "win" : "loose"}>
                            <AmountValue
                                hasSignPrefix={false}
                                amount={convertToFiat(isFiat, tokenId, value, balance)}
                                cryptoCurrency={tokenId}
                                cryptoCurrencyIcon={icon}
                                currency={isFiat ? currency : ""}
                            />
                        </TableTd>
                    ),
                    width: {
                        sm: 223,
                        md: 180,
                        lg: 223,
                    },
                },
                {
                    key: "payoutCol",
                    header: () => $t({id: "WinAmount"}),
                    col: ({currency, icon, value}, {tokenId, multiplier}) => (
                        <TableTd type={multiplier.growth === "+" ? "win" : "loose"}>
                            <AmountValue
                                hasSignPrefix={false}
                                amount={convertToFiat(isFiat, tokenId, value, balance)}
                                cryptoCurrency={tokenId}
                                cryptoCurrencyIcon={icon}
                                currency={isFiat ? currency : ""}
                            />
                        </TableTd>
                    ),
                    width: {
                        sm: 223,
                        md: 180,
                        lg: 223,
                    },
                },
            ];
        }

        return [
            {
                key: "game",
                header: () => $t({id: "Game"}),
                col: ({name, game}) => (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                        }}>
                        <GameIcon icon={'Game' + name} />
                        <div className="eclipse">{name}</div>
                    </div>
                ),
                width: {
                    sm: 200,
                    md: 180,
                    lg: 219,
                },
            },
            {
                key: "player",
                header: () => $t({id: "User"}),
                col: ({name, level}) => {
                    return <NameWithVip name={name} vipLevel={level} />;
                },
                width: {
                    sm: 150,
                    md: 180,
                    lg: 219,
                },
            },
            {
                key: "wager",
                header: () => $t({id: "BetAmount"}),
                col: ({value, currency, icon}, {tokenId, multiplier}) => (
                    <TableTd type={multiplier.growth === "+" ? "default" : "loose"}>
                        <AmountValue
                            hasSignPrefix={false}
                            amount={convertToFiat(isFiat, tokenId, value, balance)}
                            cryptoCurrency={tokenId}
                            cryptoCurrencyIcon={icon}
                            currency={isFiat ? currency : ""}
                        />
                    </TableTd>
                ),
                width: {
                    sm: 150,
                    md: 180,
                    lg: 219,
                },
            },
            {
                key: "multiplier",
                header: () => $t({id: "Multiplier"}),
                col: (data) => (
                    <TableTd
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                        }}
                        type={data.growth === "+" ? "default" : "loose"}>
                        <ActivityIcon />
                        {data.value}x
                    </TableTd>
                ),
                width: {
                    sm: 120,
                    md: 120,
                    lg: 219,
                },
            },
            {
                key: "payoutCol",
                header: () => $t({id: "Payout"}),
                col: ({value, currency, icon}, {tokenId, multiplier}) => (
                    <TableTd type={multiplier.growth === "+" ? "win" : "loose"}>
                        <AmountValue
                            hasSignPrefix={false}
                            amount={convertToFiat(isFiat, tokenId, value, balance)}
                            cryptoCurrency={tokenId}
                            cryptoCurrencyIcon={icon}
                            currency={isFiat ? currency : ""}
                        />
                    </TableTd>
                ),
                width: {
                    sm: 150,
                    md: 180,
                    lg: 219,
                },
            },
        ];
    }, [currentTab, isFiat]);

    const tabs = useMemo<ITabButton[]>(
        () => [
            {
                label: $t({id: "LiveBets"}),
                active: true,
                id: "LiveBets",
            },
            {
                label: $t({id: "HighRollers"}),
                active: false,
                id: "BigBets",
            },
            {
                label: $t({id: "MyBets"}),
                active: false,
                id: "MyBets",
            },
        ],
        [],
    );

    useEffect(() => {
        setAllBetsData([]);
        setHighRollersData([]);
        setMyBetsData([]);

        if (currentTab === "LiveBets") {
            getAllBetsData();
        } else if (currentTab === "BigBets") {
            getBigBetsData();
        } else if (currentTab === "MyBets") {
            getMyBetsData();
        }
    }, [currentTab]);

    return {
        tabs,
        columns,
        data,
        isHovered,
        setIsHovered,
        isLoading: isMyBetsLoading || isHighRollersLoading || isAllBetsLoading,
        setCurrentTab,
        setTableMaxLength,
    };
};
